import React, { useState } from 'react';
import { 
  Eye, ShieldCheck, ArrowLeft, Home, Sparkles, Download, 
  Copy, Check, FileText, Upload, X, RefreshCw, AlertCircle, 
  Lock, UserCheck, Layers, Award, Crown, CheckCircle2
} from 'lucide-react';

interface NonverbalProfilerAgentViewProps {
  language: 'uk' | 'en' | 'pl';
  onBack: () => void;
  onGoHome?: () => void;
  currentUserEmail?: string;
  useEnterpriseContext?: boolean;
  enterpriseName?: string;
}

interface AttachedFile {
  file: File;
  text?: string;
  extracted?: boolean;
  loading?: boolean;
}

const DEMO_PROMPT_UK = `Об'єкт аналізу: Кандидат на посаду фінансового директора під час співбесіди та розпитування щодо причин звільнення з попереднього місця роботи.

Опис невербальних та вербальних проявів:
1. При питанні про розходження в аудит-звіті кандидат різко відвів погляд убік-вниз, затримав дихання на 2 секунди, а потім прикрив рот долонею.
2. Голос став на тон вище, з'явилися паузи-хезитації ("е-е-ем", "ну... знаєте"), швидкість мовлення прискорилася.
3. Поза: перехрестив ноги, міцно стиснув підлокітники крісла, виник мимовільний мікромиг асиметричного підняття куточка губ (презирство/поверхнева посмішка).
4. Текст відповіді: "Я особисто не мав відношення до фінансових проводжень... це все рішення ради директорів, хоча я про це попереджав".`;

const DEMO_PROMPT_FORENSIC_HANDWRITING = `Об'єкт експертного дослідження: Рукописний текст розписки та підпис від імені директора ТОВ щодо отримання готівкових коштів у сумі 1 500 000 грн.

Питання експертизи (згідно з Інструкцією Мін'юсту № 53/5):
1. Чи виконано рукописний текст та підпис певною особою (директором) або іншою особою з наслідуванням/підробкою?
2. Чи перебувала особа під час виконання тексту та підпису у незвичному психофізіологічному стані (сильний стрес, алкогольне/медикаментозне сп'яніння, загроза/фізичний тиск) або в незвичних умовах письма (стоячи, на нерівній поверхні)?

Виявлені експертні ознаки почерку та підпису:
1. Загальні ознаки:
   - Ступінь виробленості: вище середнього, будова літер спрощена з елементами прискореного курсиву.
   - Координація рухів 1-ї групи (точність дрібних дугових та петльових елементів): суттєво порушена у словах "зобов'язуюсь" та "претензій не маю".
   - Темп письма: нерівномірний, наявні невиправдані зупинки пишучого приладу та рефлекторні розриви штрихів.
   - Натиск: диференційований, але з локальними аномальними потовщеннями в початкових штрихах.
2. Окремі ознаки (деталізація за методикою НДІСЕ):
   - Форма рухів при з'єднанні елементів букв "б", "в", "д" — петльова змінена на кутасту з тупим кутом перегину.
   - Напрямок згинальних рухів при виконанні підрядкових елементів — відхилення вліво на 15° від звичайної траєкторії.
   - Розміщення точки початку руху в заголовній букві підпису: наявний рефлекторний тремор (дрижання) лінії та подвійний дотик ручки.
3. Додаткові матеріали: вільні та експериментальні зразки почерку директора за 2024–2026 рр.`;

const DEMO_PROMPT_HANDWRITING = `Об'єкт аналізу: Рукописна пояснювальна записка працівника щодо відсутності на робочому місці під час інциденту з матеріальними цінностями.

Спостереження та параметри почерку (фото/скан):
1. Сильний нерівномірний натиск ручки на ключових фразах ("я не брав", "категорично заперечую").
2. Різка зміна нахилу літер із правого на лівий у другому абзаці при описі хронології вечора.
3. Почастішання тремору (тремтіння ліній) у словах "підтвердити" та "зобов'язуюсь".
4. Виправлення та заштриховані букви у власних іменах та часових відрізках (18:30 виправлено на 19:15).
5. Непропорційно великі поля з лівого боку та стиснутий інтервал між рядками наприкінці аркуша.`;

const DEMO_PROMPT_DRAWING = `Об'єкт аналізу: Проективний малюнок об'єкта за методикою "Людина під дощем" та "Дім-Дерево-Людина" під час профайлінгу.

Опис графічних елементів малюнка (фото/скан):
1. Людина зображена у центрі, але із надмірно сильним натиском олівця та множинним темним штрихуванням парасольки й хмар.
2. Відсутність чітко промальованих стоп та пальців на руках (руки заховані за спину або зображені як схематичні лінії).
3. Дерево має зламану гілку праворуч, оголені корені та подвійний контур стовбура.
4. Будинок зображений із зачиненими віконницями, масивним замком на дверях та відсутністю стежки.
5. Загальна композиція: зміщення об'єктів у верхній лівий кут аркуша, наявність агресивних гострих кутів на даху та хмарах.`;

export default function NonverbalProfilerAgentView({
  language,
  onBack,
  onGoHome,
  currentUserEmail,
  useEnterpriseContext,
  enterpriseName
}: NonverbalProfilerAgentViewProps) {
  const isUk = language === 'uk';
  const isAdmin = ['bbass776@gmail.com', 'irinacosmos777@gmail.com'].includes(currentUserEmail?.toLowerCase() || '');

  // State
  const [profileType, setProfileType] = useState<'deception' | 'forensic_handwriting' | 'psychoprofile' | 'graphology' | 'drawing' | 'microexpressions' | 'complex'>('deception');
  const [inputObservationText, setInputObservationText] = useState('');
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [reportResult, setReportResult] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [downloadingDocx, setDownloadingDocx] = useState(false);

  if (!isAdmin) {
    return (
      <div className="p-8 text-center bg-slate-900 border border-red-500/30 rounded-2xl text-white font-mono space-y-4 max-w-xl mx-auto my-12 shadow-2xl">
        <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto text-red-400">
          <Lock className="w-8 h-8" />
        </div>
        <h2 className="text-xl font-bold text-red-400">⛔ Доступ обмежено</h2>
        <p className="text-sm text-slate-300">
          ШІ-Експерт Невербального Профайлінгу доступний виключно для адміністраторів платформи.
        </p>
        <button 
          onClick={onGoHome || onBack}
          className="px-5 py-2.5 bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold uppercase rounded-xl transition-all cursor-pointer border border-slate-700 inline-flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>{isUk ? 'Повернутися до Хабу' : 'Back to Hub'}</span>
        </button>
      </div>
    );
  }

  // Handle file uploads & text extraction
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const newFiles = Array.from(e.target.files).map(file => ({ file, loading: true }));
    setAttachedFiles(prev => [...prev, ...newFiles]);

    for (let i = 0; i < newFiles.length; i++) {
      const fileObj = newFiles[i];
      const formData = new FormData();
      formData.append('file', fileObj.file);

      try {
        const res = await fetch('/api/extract-text', {
          method: 'POST',
          body: formData
        });
        if (res.ok) {
          const data = await res.json();
          setAttachedFiles(prev => prev.map(item => item.file === fileObj.file ? { ...item, text: data.text || '', extracted: true, loading: false } : item));
        } else {
          setAttachedFiles(prev => prev.map(item => item.file === fileObj.file ? { ...item, loading: false } : item));
        }
      } catch {
        setAttachedFiles(prev => prev.map(item => item.file === fileObj.file ? { ...item, loading: false } : item));
      }
    }
  };

  const handleRemoveFile = (fileToRemove: File) => {
    setAttachedFiles(prev => prev.filter(item => item.file !== fileToRemove));
  };

  const handleRunAnalysis = async () => {
    if (!inputObservationText.trim() && attachedFiles.length === 0) {
      setErrorMsg(isUk ? 'Будь ласка, введіть опис невербальних спостережень або підгрузіть скан/фото/файл.' : 'Please enter nonverbal observation notes or attach file.');
      return;
    }

    setIsProcessing(true);
    setErrorMsg(null);

    let combinedText = inputObservationText.trim();
    attachedFiles.forEach(item => {
      if (item.text) {
        combinedText += `\n\n--- ЗМІСТ ФАЙЛУ (${item.file.name}) ---\n${item.text}`;
      }
    });

    try {
      const res = await fetch('/api/run-templated-crew', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          crewName: 'nonverbal_profiler_crew',
          useEnterpriseContext: false,
          inputs: {
            profile_type: profileType,
            observation_notes: combinedText,
            enterprise_context: ''
          }
        })
      });

      if (res.ok) {
        const data = await res.json();
        if (data.jobId) {
          const pollJob = async () => {
            try {
              const statusRes = await fetch(`/api/crew-status/${data.jobId}`);
              if (!statusRes.ok) {
                throw new Error('Помилка перевірки статусу завдання.');
              }
              const statusData = await statusRes.json();
              if (statusData.status === 'completed') {
                const rawReport = statusData.report || statusData.result || '';
                const resText = typeof rawReport === 'string'
                  ? rawReport
                  : (typeof rawReport === 'object' && rawReport !== null
                      ? (rawReport.report || rawReport.result || JSON.stringify(rawReport, null, 2))
                      : String(rawReport));

                if (!resText) {
                  throw new Error('Сервер повернув порожній результат звіту.');
                }
                setReportResult(resText);
                setIsProcessing(false);
              } else if (statusData.status === 'failed') {
                setErrorMsg(statusData.error || 'Помилка генерації звіту.');
                setIsProcessing(false);
              } else {
                setTimeout(pollJob, 2000);
              }
            } catch (pollErr: any) {
              console.error(pollErr);
              setErrorMsg(pollErr.message || 'Помилка опитування завдання.');
              setIsProcessing(false);
            }
          };
          setTimeout(pollJob, 1000);
        } else {
          setReportResult(data.report || data.result || data.output || data.response);
          setIsProcessing(false);
        }
      } else {
        const errData = await res.json().catch(() => ({}));
        setErrorMsg(errData.error || errData.message || 'Не вдалося провести невербальний аналіз.');
        setIsProcessing(false);
      }
    } catch (err: any) {
      console.error('Nonverbal analysis error:', err);
      setErrorMsg(err.message || 'Помилка мережі при зорганізації з\'єднанні з ШІ-сервером.');
      setIsProcessing(false);
    }
  };

  const handleDownloadDocx = async () => {
    if (!reportResult) return;
    setDownloadingDocx(true);
    try {
      const fileName = `Profiling_Report_${Date.now()}`;
      const res = await fetch('/api/generate-docx', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: reportResult,
          fileName
        })
      });

      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${fileName}.docx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } else {
        const errData = await res.json().catch(() => ({}));
        alert(errData.error || (isUk ? 'Не вдалося згенерувати Word файл.' : 'Failed to generate Word file.'));
      }
    } catch (err: any) {
      console.error('DOCX download error:', err);
      alert(isUk ? 'Помилка при завантаженні файлу Word.' : 'Error downloading Word file.');
    } finally {
      setDownloadingDocx(false);
    }
  };

  const handleCopyText = () => {
    if (!reportResult) return;
    navigator.clipboard.writeText(reportResult);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const isPl = language === 'pl';
  const isEn = language === 'en';

  return (
    <div className="space-y-8 animate-fade-in pb-12 font-mono">
      {/* Top Header Bar */}
      <div className="flex items-center justify-between gap-4 flex-wrap bg-slate-900 border border-amber-500/30 rounded-xl p-4 text-white shadow-lg">
        <div className="flex items-center gap-2">
          <button
            onClick={onBack}
            className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-amber-300 text-xs font-bold uppercase rounded-lg transition-colors flex items-center gap-2 cursor-pointer border border-amber-500/30"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>{isPl ? '← Powrót do Centrum Admina' : isUk ? '← До Адмін-Центру' : '← Back to Admin Center'}</span>
          </button>

          {onGoHome && (
            <button
              onClick={onGoHome || onBack}
              className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold uppercase rounded-lg transition-colors flex items-center gap-2 cursor-pointer border border-slate-700"
            >
              <Home className="w-4 h-4" />
              <span>{isPl ? '🏠 Strona Główna' : isUk ? '🏠 На головну' : '🏠 Main Hub'}</span>
            </button>
          )}
        </div>

        <div className="flex items-center gap-2">
          <span className="px-2.5 py-1 text-[10px] font-extrabold uppercase bg-amber-500/20 text-amber-300 border border-amber-400/40 rounded-md flex items-center gap-1">
            <Crown className="w-3.5 h-3.5 text-amber-400" />
            👑 ADMIN EXCLUSIVE
          </span>
          <span className="px-2.5 py-1 text-[10px] font-extrabold uppercase bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 rounded-md flex items-center gap-1">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
            FACS, GRAPHOLOGY & DRAWING AUDIT
          </span>
        </div>
      </div>

      {/* Hero Banner */}
      <div className="relative bg-gradient-to-r from-slate-950 via-amber-950 to-slate-900 border border-amber-500/50 rounded-2xl p-6 sm:p-8 text-white shadow-2xl overflow-hidden">
        <div className="absolute right-0 top-0 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 max-w-3xl space-y-3">
          <div className="flex items-center gap-2">
            <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-amber-500/30 text-amber-300 border border-amber-400/40 rounded">
              {isPl ? 'FACS • KINEZYKA • GRAFOLOGIA PISMA • ANALIZA RYSUNKÓW • DETEKCJA KŁAMSTWA' : 'FACS • КІНЕСИКА • ГРАФОЛОГІЯ ПОЧЕРКУ • АНАЛІЗ МАЛЮНКІВ • ДЕТЕКЦІЯ БРЕХНІ'}
            </span>
          </div>

          <h1 className="text-2xl sm:text-4xl font-extrabold tracking-tight uppercase leading-tight bg-gradient-to-r from-amber-200 via-yellow-100 to-amber-400 bg-clip-text text-transparent">
            {isPl
              ? 'EKSPERT PROFILOWANIA NIEWERBALNEGO, GRAFOLOGII, RYSUNKÓW I WYKRYWANIA KŁAMSTWA'
              : isUk 
              ? 'ЕКСПЕРТ НЕВЕРБАЛЬНОГО ПРОФАЙЛІНГУ, ГРАФОЛОГІЇ, МАЛЮНКІВ & ДЕТЕКЦІЇ БРЕХНІ' 
              : 'AI NONVERBAL PROFILING, GRAPHOLOGY, DRAWINGS & LIE DETECTION EXPERT'}
          </h1>

          <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
            {isPl
              ? 'Autonomiczny system AI do analizy sygnałów niewerbalnych, mikroekspresji mimicznych (FACS), kinezyki, grafologii pisma ręcznego ze skanu/zdjęcia, testów rysunkowych oraz markerów nieszczerości. Generuje raport ekspercki w formacie Word (.docx).'
              : isUk 
              ? 'Комплексна автономна система аналізу невербальних сигналів, мікровиразів обличчя (FACS), кінесики, ШІ-графології почерку за сканом/фото, проективного аналізу малюнків та маркерів обману. Формує експертну матрицю щирості й чистий аналітичний звіт у Word (.docx).'
              : 'Autonomous AI system for analyzing nonverbal signals, facial microexpressions (FACS), kinesics, handwriting graphology, projective drawing tests, and lie detection markers. Generates clean analytical reports in Word (.docx).'}
          </p>
        </div>
      </div>

      {/* Main Input Form & Configuration */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 text-white space-y-6 shadow-xl">
        <h2 className="text-base font-extrabold text-amber-300 uppercase tracking-wide flex items-center gap-2">
          <Eye className="w-5 h-5 text-amber-400" />
          <span>{isUk ? '1. Обирайте Профіль Аналізу & Вхідні Спостереження / Файли' : '1. Select Analysis Profile & Input Observations / Files'}</span>
        </h2>

        {/* Profile Selector */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {[
            { id: 'deception', titleUk: '🕵️ Детекція брехні & Верифікація щирості', descUk: 'Виявлення суперечностей, вербальних/невербальних маркерів обману' },
            { id: 'forensic_handwriting', titleUk: '⚖️ Судово-почеркознавча експертиза & стан', descUk: 'Наукова ідентифікація особи, збиваючих факторів (стрес, алкоголь, тиск, імітація) за нормами Мін\'юсту № 53/5' },
            { id: 'graphology', titleUk: '✍️ Графологія & Психологічний аналіз почерку', descUk: 'Аналіз натиску, нахилу, ліній, виправлень та маркерів внутрішнього стресу' },
            { id: 'drawing', titleUk: '🎨 Проективний аналіз малюнків', descUk: 'Діагностика за малюнками (Дім-Дерево-Людина, тривожність, агресія)' },
            { id: 'psychoprofile', titleUk: '🧠 Психопрофайлінг & Характерологія', descUk: 'Картування рис характеру, емоційної стійкості та поведінки' },
            { id: 'microexpressions', titleUk: '🎭 Аналіз мікровиразів обличчя (FACS)', descUk: 'Розшифровка мимовільних мікроспалахів емоцій за Полом Екманом' },
            { id: 'complex', titleUk: '📜 Комплексна невербально-почеркознавча експертиза', descUk: 'Поєднаний аналіз мови тіла, міміки FACS, криміналістичного почерку та малюнків' }
          ].map((profile) => (
            <div
              key={profile.id}
              onClick={() => setProfileType(profile.id as any)}
              className={`p-4 rounded-xl border transition-all cursor-pointer space-y-1.5 ${
                profileType === profile.id
                  ? 'bg-amber-500/20 border-amber-400 text-amber-200 shadow-md shadow-amber-500/10'
                  : 'bg-slate-950 border-slate-800 text-slate-300 hover:border-slate-700'
              }`}
            >
              <div className="text-xs font-extrabold uppercase">{profile.titleUk}</div>
              <div className="text-[11px] text-slate-400 leading-snug">{profile.descUk}</div>
            </div>
          ))}
        </div>

        {/* Text Input Area & Demo Presets Buttons */}
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <label className="text-xs font-extrabold uppercase text-amber-300 flex items-center gap-1.5">
              <span>{isUk ? 'Опис невербальних спостережень / Матеріалів судово-почеркознавчої експертизи' : 'Observation Notes / Forensic Handwriting & Profiling Data'}</span>
            </label>

            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => {
                  setProfileType('forensic_handwriting');
                  setInputObservationText(DEMO_PROMPT_FORENSIC_HANDWRITING);
                }}
                className="text-[11px] font-bold text-emerald-400 hover:text-emerald-300 underline cursor-pointer flex items-center gap-1"
              >
                <Sparkles className="w-3 h-3" />
                <span>{isUk ? '⚖️ Судово-почеркознавча демо' : '⚖️ Forensic Handwriting Demo'}</span>
              </button>

              <button
                onClick={() => {
                  setProfileType('deception');
                  setInputObservationText(DEMO_PROMPT_UK);
                }}
                className="text-[11px] font-bold text-amber-400 hover:text-amber-300 underline cursor-pointer flex items-center gap-1"
              >
                <Sparkles className="w-3 h-3" />
                <span>{isUk ? '🕵️ Співбесіда (FACS)' : '🕵️ Interview Demo'}</span>
              </button>

              <button
                onClick={() => {
                  setProfileType('graphology');
                  setInputObservationText(DEMO_PROMPT_HANDWRITING);
                }}
                className="text-[11px] font-bold text-amber-400 hover:text-amber-300 underline cursor-pointer flex items-center gap-1"
              >
                <Sparkles className="w-3 h-3" />
                <span>{isUk ? '✍️ Почерк / Записка' : '✍️ Handwriting Demo'}</span>
              </button>

              <button
                onClick={() => {
                  setProfileType('drawing');
                  setInputObservationText(DEMO_PROMPT_DRAWING);
                }}
                className="text-[11px] font-bold text-amber-400 hover:text-amber-300 underline cursor-pointer flex items-center gap-1"
              >
                <Sparkles className="w-3 h-3" />
                <span>{isUk ? '🎨 Малюнок (Проективний)' : '🎨 Drawing Demo'}</span>
              </button>
            </div>
          </div>

          <textarea
            rows={6}
            value={inputObservationText}
            onChange={(e) => setInputObservationText(e.target.value)}
            placeholder={isUk 
              ? 'Введіть текстовий опис поведінки об\'єкта, мікровиразів обличчя, або завантажте фото рукописного тексту/малюнка та додайте описові деталі...'
              : 'Enter behavioral description, facial microexpressions, or upload handwriting/drawing photos with details...'}
            className="w-full bg-slate-950 border border-slate-800 rounded-xl p-4 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-amber-400 font-mono leading-relaxed"
          />
        </div>

        {/* Multi-file Upload Module */}
        <div className="space-y-3 pt-2 border-t border-slate-800">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className="text-xs font-extrabold uppercase text-slate-300 flex items-center gap-1.5">
              <Upload className="w-4 h-4 text-amber-400" />
              <span>{isUk ? 'Підгрузка матеріалів (Фото, Відео, Стенограми PDF/DOCX/TXT)' : 'Attach Materials (Photo, Video, Transcripts)'}</span>
            </span>
            <label className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-amber-300 text-xs font-bold uppercase rounded-lg transition-colors cursor-pointer border border-slate-700 inline-flex items-center gap-1.5">
              <Upload className="w-3.5 h-3.5" />
              <span>{isUk ? '➕ Додати файли' : '➕ Add Files'}</span>
              <input 
                type="file" 
                multiple 
                onChange={handleFileChange} 
                className="hidden" 
                accept=".txt,.pdf,.docx,.doc,.jpg,.jpeg,.png,.webp,.mp4,.webm"
              />
            </label>
          </div>

          {attachedFiles.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-1">
              {attachedFiles.map((item, idx) => (
                <div key={idx} className="flex items-center gap-2 px-3 py-1.5 bg-slate-950 border border-slate-800 rounded-lg text-xs text-slate-200">
                  <FileText className="w-3.5 h-3.5 text-amber-400" />
                  <span className="truncate max-w-[200px] font-bold">{item.file.name}</span>
                  {item.loading ? (
                    <RefreshCw className="w-3 h-3 text-amber-400 animate-spin" />
                  ) : item.extracted ? (
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                  ) : null}
                  <button onClick={() => handleRemoveFile(item.file)} className="text-slate-400 hover:text-red-400 cursor-pointer ml-1">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {errorMsg && (
          <div className="p-3.5 bg-red-500/20 border border-red-500/40 rounded-xl text-red-300 text-xs flex items-start gap-2">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>{errorMsg}</span>
          </div>
        )}

        {/* Action Button */}
        <button
          onClick={handleRunAnalysis}
          disabled={isProcessing}
          className="w-full py-4 bg-gradient-to-r from-amber-600 via-yellow-600 to-amber-500 hover:from-amber-500 hover:to-yellow-500 text-slate-950 font-extrabold text-xs uppercase tracking-wider rounded-xl transition-all shadow-xl shadow-amber-600/30 flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 hover:scale-[1.01]"
        >
          {isProcessing ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin text-slate-950" />
              <span>{isUk ? 'Аналіз невербальних маркерів та формування звіту...' : 'Analyzing nonverbal markers & generating report...'}</span>
            </>
          ) : (
            <>
              <Eye className="w-4 h-4 text-slate-950" />
              <span>{isUk ? '👁️ Проаналізувати & Сформувати Профайлінговий Звіт' : '👁️ Analyze & Generate Profiling Report'}</span>
            </>
          )}
        </button>
      </div>

      {/* Results Section & DSTU Word Export */}
      {reportResult && (
        <div className="bg-slate-900 border border-amber-500/40 rounded-2xl p-6 text-white space-y-6 shadow-2xl animate-fade-in">
          <div className="flex items-center justify-between gap-4 flex-wrap border-b border-slate-800 pb-4">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-emerald-400 animate-pulse" />
              <h2 className="text-base font-extrabold text-amber-300 uppercase tracking-wide">
                {isUk ? '2. Профайлінговий Звіт & Матриця Верифікації' : '2. Profiling Report & Verification Matrix'}
              </h2>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={handleCopyText}
                className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold uppercase rounded-lg transition-colors flex items-center gap-1.5 cursor-pointer border border-slate-700"
              >
                {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                <span>{copied ? (isUk ? 'Скопійовано' : 'Copied') : (isUk ? 'Копіювати' : 'Copy')}</span>
              </button>

              <button
                onClick={handleDownloadDocx}
                disabled={downloadingDocx}
                className="px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white text-xs font-extrabold uppercase rounded-lg transition-all flex items-center gap-2 cursor-pointer shadow-lg shadow-emerald-600/20 disabled:opacity-50"
              >
                {downloadingDocx ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                <span>{isUk ? '📥 Завантажити Word (.docx)' : '📥 Export Word (.docx)'}</span>
              </button>
            </div>
          </div>

          <div className="bg-slate-950 border border-slate-800 rounded-xl p-6 text-slate-200 text-xs leading-relaxed space-y-4 font-mono overflow-x-auto whitespace-pre-wrap">
            {reportResult}
          </div>
        </div>
      )}
    </div>
  );
}
